import React from "react";
import { globalRoles } from "../utils/globalRoles";
import { FormattedMessage } from "react-intl";
import GreytHRSupport from "../views/Dashboard/GreytHRSupport";
import { LottieImage } from "../components/common";
import loadingImage from "../utils/loader.json";
import features from "../utils/features.json";

const AllComponents = React.lazy(() => import("../views/Storyboard"));
const Dashboard = React.lazy(() => import("../views/Dashboard"));
const DashboardScreens = React.lazy(() =>
  import("../views/Dashboard/DashboardScreen")
);
const TeamDetails = React.lazy(() =>
  import("../views/Dashboard/Teams/Details")
);
const Clients = React.lazy(() => import("../views/Dashboard/Clients"));
const ClientDetails = React.lazy(() =>
  import("../views/Dashboard/Clients/Details")
);
const ClientForm = React.lazy(() => import("../views/Dashboard/Clients/forms"));
const Teams = React.lazy(() => import("../views/Dashboard/Teams"));
const TeamForm = React.lazy(() => import("../views/Dashboard/Teams/Forms"));
const RolesManagement = React.lazy(() => import("../views/Dashboard/Roles"));
const HelpDesk = React.lazy(() => import("../views/Dashboard/HelpDesk"));
const Profile = React.lazy(() => import("../views/Dashboard/Profile"));
const NewsAndAlerts = React.lazy(() => import("../views/Dashboard/NewsAlerts"));
const RoleSyncHistory = React.lazy(() =>
  import("../views/Dashboard/Roles/RoleSyncHistory")
);
const Settings = React.lazy(() => import("../views/Dashboard/Settings"));
const YourTickets = React.lazy(() => import("../views/Dashboard/YourTickets"));
const ManageSLA = React.lazy(() => import("../views/Dashboard/ManageSLA"));
const CreateTemplate = React.lazy(() => import("../views/Dashboard/SLA/CreateTemplate"));
const WorkCenter = React.lazy(() => import("../views/Dashboard/WorkCenter"));
const InvoiceHub = React.lazy(() => import("../views/Dashboard/InvoiceHub"));
const OverviewInfo = React.lazy(() => import("../views/Dashboard/Overview"));
const POI = React.lazy(() => import("../views/Dashboard/POI"));
const ReImbursement = React.lazy(() => import("../views/Dashboard/Reimbursement"));
const PayrollOverview = React.lazy(() => import("../views/Dashboard/PayrollOverview"));
const FBPOverview = React.lazy(() => import("../views/Dashboard/FBPOverview"));
const ITManagement = React.lazy(() =>import("../views/Dashboard/ITManagement"));

const PANStatus = React.lazy(() => import("../views/Dashboard/PAN"));

export const Loader = () => {
  return (
    <div className="flex h-screen text-center items-center justify-center mx-8">
      <LottieImage
        comingsoonimg={"true"}
        style={{ height: 125 }}
        src={loadingImage}
      />
    </div>
  );
}

export const adminRoutes = [
  {
    path: "components",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <AllComponents />
        </Dashboard>
      </React.Suspense>
    ),
    title: "Components",
    subTitle: "",
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "dashboard",
    element: (
      <React.Suspense fallback={<Loader />}  >
        <Dashboard>
          <DashboardScreens />
        </Dashboard>
      </React.Suspense>
    ),
    title: "Hello",
    subTitle: "",
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "create-client",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ClientForm />
        </Dashboard>
      </React.Suspense>
    ),
    title: "Create Client",
    subTitle: "",
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    region: ["Other", "India"],
  },
  {
    path: "teams",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <Teams />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="TEAMS.TITLE" />,
    subTitle: <FormattedMessage id="TEAMS.SUB_TITLE" />,
    permissions: [
      globalRoles.admin,
      globalRoles.superAdmin,
      globalRoles.manager,
    ],
    region: ["Other", "India"],
  },
  {
    path: "clients",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <Clients />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="CLIENTS.TITLE" />,
    subTitle: <FormattedMessage id="CLIENTS.SUB_TITLE" />,
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "update/client",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ClientForm />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="UPDATE_CLIENTS.TITLE" />,
    subTitle: "",
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    region: ["Other", "India"],
  },
  {
    path: "teams/create",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <TeamForm formType="create" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="TEAMS.CREATE.TITLE" />,
    subTitle: "",
    permissions: [
      globalRoles.admin,
      globalRoles.superAdmin,
      globalRoles.manager,
    ],
    region: ["Other", "India"],
  },
  {
    path: "clients/details",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ClientDetails />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="CLIENT.DETAILS.TITLE" />,
    mainTitle: "Client",
    subTitle: "",
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "teams/details",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <TeamDetails />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="TEAMS.DETAILS.TITLE" />,
    mainTitle: <FormattedMessage id="TEAMS.DETAILS.TITLE" />,
    subTitle: "",
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "teams/update",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <TeamForm formType="update" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="TEAMS.EDIT.TITLE" />,
    subTitle: "",
    permissions: [
      globalRoles.admin,
      globalRoles.superAdmin,
      globalRoles.manager,
    ],
    region: ["Other", "India"],
  },
  {
    path: "help-desk",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <HelpDesk />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="HELPDESK.TITLE" />,
    subTitle: <FormattedMessage id="HELPDESK.SUBTITLE" />,
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "news-alerts",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <NewsAndAlerts />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="NEWS_AND_ALERTS.TITLE" />,
    subTitle: <FormattedMessage id="NEWS_AND_ALERTS.SUBTITLE" />,
    permissions: ["*"],
    region: ["India"],
  },
  {
    path: "profile",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <Profile />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="PROFILE.TITLE" />,
    subTitle: "",
    permissions: ["*"],
    region: ["Other", "India"],
  },
  {
    path: "roles",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <RolesManagement />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="ROLES.TITLE" />,
    subTitle: <FormattedMessage id="ROLES.SUBTITLE" />,
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    region: ["Other", "India"],
  },
  {
    path: "settings",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <Settings />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="SETTINGS.TITLE" />,
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    region: ["Other", "India"],
  },
  {
    path: "roles/sync-history",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <RoleSyncHistory />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="ROLE.HISTORY.TITLE" />,
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    region: ["Other", "India"],
  },
  {
    path: "your-tickets",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <YourTickets />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.TICKETS.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "manage-sla",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ManageSLA />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.SLA.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "manage-sla/create-milestones",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <CreateTemplate mode="create" page="sla" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.SLA.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "manage-sla/update-milestones",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <CreateTemplate mode="update" page="sla" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.SLA.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "settings/create-template",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <CreateTemplate mode="create" page="settings" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.SLA.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "settings/update-template",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <CreateTemplate mode="update" page="settings" />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.PRO.SLA.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "workcenter",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <WorkCenter />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="WORKCENTER.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "invoice-hub",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <InvoiceHub />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="INVOICEHUB.PAGE.TITLE" />,
    permissions: [globalRoles.admin, globalRoles.superAdmin],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "greytHR-support",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <GreytHRSupport />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="GREYTHR.SUPPORT.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: <FormattedMessage id="GREYTHR.SUPPORT.PAGE.SUBTITLE" />,
    region: ["Other", "India"],
  },
  {
    path: "payroll-overview",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <PayrollOverview />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="OVERVIEW.PAYROLL.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "poi-overview",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <POI />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="OVERVIEW.POI.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["India"],
  },
  {
    path: "pan-status",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <PANStatus />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="PAN_STATUS.PAGE.TITLE" />,
    permissions:["*"],
    subTitle: "",
    region: ["India"],
  },
  {
    path: "fbp-management",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <OverviewInfo feature={features?.fpb_management} />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="OVERVIEW.FPB.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "it-management",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ITManagement />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="OVERVIEW.IT_DECLARATION.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["India"],
  },
  {
    path: "tasks",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <OverviewInfo feature={features?.tasks} />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="OVERVIEW.TASK.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "re-imbursements",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <ReImbursement />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="REIMBURSEMENT.OVERVIEW.PAGE.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["Other", "India"],
  },
  {
    path: "fbp-overview",
    element: (
      <React.Suspense fallback={<Loader />}>
        <Dashboard>
          <FBPOverview />
        </Dashboard>
      </React.Suspense>
    ),
    title: <FormattedMessage id="FBP.OVERVIEW.TITLE" />,
    permissions: ["*"],
    subTitle: "",
    region: ["India"],
  },
];
